import {Divider, useMediaQuery} from '@mui/material'
import {getAuth} from 'firebase/auth'
import React from 'react'
import {Link as RouterLink, useLocation} from 'react-router-dom'
import {isActivePath} from '../utils/isActivePath'
import {AppBar} from '../../webui/AppBar'
import {Box} from '../../webui/Box'
import {Container} from '../../webui/Container'
import {Grid} from '../../webui/Grid'
import {IconButton} from '../../webui/IconButton'
import {AccountCircle} from '../../webui/icons/AccountCircle'
import {Link} from '../../webui/Link'
import {Menu} from '../../webui/Menu'
import {MenuItem} from '../../webui/MenuItem'
import {Typography} from '../../webui/Typography'
import {AuthUser} from '../auth/AuthUser'
import {Routes} from '../Routes'
import {HeaderLink, headerLinks} from './headerLinks'
import {ResponsivePageLinksMenu} from './ResponsivePageLinksMenu'
import {sortHeaderLinks} from './sortHeaderLinks'

interface HeaderProps {
  user: AuthUser | null
}

const responsiveHeaderLinks = sortHeaderLinks(headerLinks)
const isMostUsedLink = (link: HeaderLink) =>
  [
    Routes.ACTIONS,
    Routes.ALL_ACTIVITY_AREAS,
    Routes.ALL_BOXES,
    Routes.ALL_PRODUCTS,
    Routes.ALL_RENTALS,
    Routes.STATISTICS,
    Routes.ALL_USERS,
  ].includes(link.path)

const desktopBurgerHeaderLinks = sortHeaderLinks(headerLinks.filter((link) => !isMostUsedLink(link)))
const desktopHeaderLinks = sortHeaderLinks(headerLinks.filter(isMostUsedLink))

export const Header: React.FC<HeaderProps> = ({user}) => {
  const mobile = useMediaQuery('(max-width:1024px)')
  const {pathname} = useLocation()

  const [loggedInMenuAnchorEl, setLoggedInMenuAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(loggedInMenuAnchorEl)

  const handleOpenLoggedInMenu = (event: React.MouseEvent<HTMLElement>) => {
    setLoggedInMenuAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setLoggedInMenuAnchorEl(null)
  }

  const logout = async () => {
    await getAuth().signOut()
  }

  return (
    <Box display={'flex'} flexGrow={1} zIndex={1000}>
      <AppBar position="static">
        <Grid container alignItems="center" justifyContent={'space-between'} height={'48px'}>
          <Box flexDirection={'row'} display={'flex'} alignItems={'center'}>
            {mobile ? (
              <ResponsivePageLinksMenu headerLinks={responsiveHeaderLinks} />
            ) : (
              <ResponsivePageLinksMenu headerLinks={desktopBurgerHeaderLinks} />
            )}
            <Container className="nav-link-container">
              <Link color={'inherit'} className="nav-link" component={RouterLink} to={Routes.HOME}>
                Piffl
              </Link>
            </Container>
            <Divider orientation="vertical" flexItem />
            {!mobile &&
              desktopHeaderLinks.map((link, index) => {
                const active = isActivePath(pathname, link.path)
                return (
                  <React.Fragment key={link.path}>
                    {index !== 0 && <Divider orientation="vertical" flexItem />}
                    <Container className="nav-link-container">
                      <Link
                        noWrap={true}
                        color={'inherit'}
                        component={RouterLink}
                        to={link.path}
                        className="nav-link"
                        sx={{fontWeight: active ? 'bold' : 'normal'}}
                      >
                        {link.label}
                      </Link>
                    </Container>
                  </React.Fragment>
                )
              })}
          </Box>
          <div>
            <>
              <IconButton
                aria-label="account of current user"
                aria-controls="logged-in-menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenLoggedInMenu}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="logged-in-menu-appbar"
                anchorEl={loggedInMenuAnchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={handleClose}
              >
                <div>
                  <Box p={2}>
                    <Typography>{user?.email ?? ''}</Typography>
                  </Box>
                </div>
                <MenuItem onClick={logout}>Log out</MenuItem>
              </Menu>
            </>
          </div>
        </Grid>
      </AppBar>
    </Box>
  )
}
